/**
 * Leitura do histórico do imóvel (ActivityLog) para apresentação.
 *
 * Os registos novos (`metadata.auditVersion >= 2`) trazem as alterações já
 * calculadas no servidor: campo técnico, chave de tradução, tipo de valor,
 * antes/depois e texto legível guardado no momento. Os registos antigos
 * (`{ label, before, after }`) continuam a ser mostrados como estavam.
 */

export type Translate = (key: string, params?: Record<string, any>) => string;

export type HistoryCategory =
  | "view"
  | "create"
  | "update"
  | "price"
  | "status"
  | "publication"
  | "validation"
  | "associations"
  | "media"
  | "document"
  | "share"
  | "note"
  | "delete";

export interface HistoryChangeVm {
  field: string;
  label: string;
  suffix: string | null;
  changeType: "added" | "removed" | "modified";
  before: string;
  after: string;
  details: Array<{ label: string; before: string; after: string }>;
  longText: boolean;
}

const CATEGORY_BY_ACTION: Record<string, HistoryCategory> = {
  PROPERTY_VIEW: "view",
  PROPERTY_CREATE: "create",
  PROPERTY_DUPLICATE: "create",
  PROPERTY_RESALE: "create",
  PROPERTY_DELETE: "delete",
  PROPERTY_PUBLISH: "publication",
  PROPERTY_UNPUBLISH: "publication",
  PROPERTY_VALIDATION_REQUESTED: "validation",
  PROPERTY_APPROVED: "validation",
  PROPERTY_REJECTED: "validation",
  PROPERTY_REFERENCE_GENERATED: "update",
  PROPERTY_SHARE: "share",
  PROPERTY_NOTE_CREATED: "note",
};

export const HISTORY_CATEGORY_ICON: Record<HistoryCategory, string> = {
  view: "ri-eye-line",
  create: "ri-add-circle-line",
  update: "ri-edit-2-line",
  price: "ri-price-tag-3-fill",
  status: "ri-exchange-line",
  publication: "ri-global-line",
  validation: "ri-shield-check-line",
  associations: "ri-team-line",
  media: "ri-image-line",
  document: "ri-file-text-fill",
  share: "ri-share-forward-line",
  note: "ri-sticky-note-line",
  delete: "ri-delete-bin-line",
};

/** Filtro "Tipo" do separador: cada opção é um conjunto de ações. */
export const HISTORY_TYPE_FILTERS: Array<{
  value: string;
  label: string;
  actions: string[];
}> = [
  {
    value: "edits",
    label: "t-property-history-type-edits",
    actions: [
      "PROPERTY_CREATE",
      "PROPERTY_DUPLICATE",
      "PROPERTY_RESALE",
      "PROPERTY_UPDATE",
      "PROPERTY_REFERENCE_GENERATED",
      "PROPERTY_DELETE",
    ],
  },
  {
    value: "status",
    label: "t-property-history-type-status",
    actions: [
      "PROPERTY_PUBLISH",
      "PROPERTY_UNPUBLISH",
      "PROPERTY_VALIDATION_REQUESTED",
      "PROPERTY_APPROVED",
      "PROPERTY_REJECTED",
    ],
  },
  {
    value: "media",
    label: "t-property-history-type-media",
    actions: [
      "PROPERTY_IMAGE_ADDED",
      "PROPERTY_IMAGE_REPLACED",
      "PROPERTY_IMAGE_REMOVED",
      "PROPERTY_IMAGES_REORDERED",
      "PROPERTY_IMAGE_VIEWED",
      "PROPERTY_IMAGE_DOWNLOAD_REQUESTED",
    ],
  },
  {
    value: "documents",
    label: "t-property-history-type-documents",
    actions: [
      "PROPERTY_DOCUMENT_ADDED",
      "PROPERTY_DOCUMENT_REPLACED",
      "PROPERTY_DOCUMENT_REMOVED",
      "PROPERTY_DOCUMENT_VIEWED",
      "PROPERTY_DOCUMENT_DOWNLOAD_REQUESTED",
      "PROPERTY_DOCUMENT_DOWNLOADED",
    ],
  },
  {
    value: "views",
    label: "t-property-history-type-views",
    actions: [
      "PROPERTY_VIEW",
      "PROPERTY_IMAGE_VIEWED",
      "PROPERTY_DOCUMENT_VIEWED",
    ],
  },
  {
    value: "other",
    label: "t-property-history-type-other",
    actions: ["PROPERTY_SHARE", "PROPERTY_NOTE_CREATED"],
  },
];

export function isAuditV2(metadata: any): boolean {
  return Number(metadata?.auditVersion) >= 2;
}

export function categoryOf(action: string, metadata: any): HistoryCategory {
  if (CATEGORY_BY_ACTION[action]) return CATEGORY_BY_ACTION[action];
  if (action.startsWith("PROPERTY_IMAGE")) return "media";
  if (action.startsWith("PROPERTY_DOCUMENT")) return "document";

  const groups: string[] = Array.isArray(metadata?.groups) ? metadata.groups : [];
  if (groups.length && groups.every((g) => g === "price")) return "price";
  if (groups.includes("status")) return "status";
  if (
    groups.length &&
    groups.every((g) => ["agents", "owners", "associations", "agency"].includes(g))
  ) {
    return "associations";
  }
  return "update";
}

function isEmpty(value: any): boolean {
  return value === null || value === undefined || value === "";
}

const MONEY = (locale: string, currency = "EUR") =>
  new Intl.NumberFormat(locale, { style: "currency", currency, maximumFractionDigits: 2 });

export function formatAuditValue(
  value: any,
  valueType: string,
  translate: Translate,
  locale: string,
  label?: string | null
): string {
  if (!isEmpty(label)) return `${label}`;
  if (isEmpty(value)) return "—";

  switch (valueType) {
    case "boolean":
      return translate(value === true || value === "true" ? "t-yes" : "t-no");
    case "money":
      return Number.isFinite(Number(value)) ? MONEY(locale).format(Number(value)) : `${value}`;
    case "area":
      return Number.isFinite(Number(value))
        ? `${new Intl.NumberFormat(locale).format(Number(value))} m²`
        : `${value}`;
    case "percent":
      return Number.isFinite(Number(value))
        ? `${new Intl.NumberFormat(locale).format(Number(value))}%`
        : `${value}`;
    case "number":
      return Number.isFinite(Number(value))
        ? new Intl.NumberFormat(locale).format(Number(value))
        : `${value}`;
    case "date":
    case "datetime": {
      const date = new Date(value);
      if (isNaN(date.getTime())) return `${value}`;
      return valueType === "date"
        ? date.toLocaleDateString(locale)
        : date.toLocaleString(locale, { dateStyle: "short", timeStyle: "short" });
    }
    case "longtext": {
      const text = stripHtml(`${value}`);
      return text.length > 280 ? `${text.slice(0, 280)}…` : text;
    }
    default:
      return typeof value === "object" ? JSON.stringify(value) : `${value}`;
  }
}

function stripHtml(text: string): string {
  return text.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
}

/** Alterações de um registo, prontas a mostrar (registo novo ou antigo). */
export function buildChangeVms(
  metadata: any,
  translate: Translate,
  locale: string
): HistoryChangeVm[] {
  const changes: any[] = Array.isArray(metadata?.changes) ? metadata.changes : [];

  if (!isAuditV2(metadata)) {
    // Formato antigo: `{ label, before, after }` já em texto.
    return changes.map((change) => ({
      field: change?.label ?? "",
      label: change?.label ?? "",
      suffix: null,
      changeType: isEmpty(change?.before)
        ? "added"
        : isEmpty(change?.after)
        ? "removed"
        : "modified",
      before: isEmpty(change?.before) ? "—" : `${change.before}`,
      after: isEmpty(change?.after) ? "—" : `${change.after}`,
      details: [],
      longText: false,
    }));
  }

  return changes.map((change) => {
    const isItem = change?.valueType === "item";
    const details = (change?.subChanges ?? []).map((sub: any) => ({
      label: sub.label,
      before: formatAuditValue(sub.before, sub.valueType, translate, locale),
      after: formatAuditValue(sub.after, sub.valueType, translate, locale),
    }));

    let before: string;
    let after: string;
    if (isItem) {
      // Elemento de lista (agente, proprietário, idioma…): entrou ou saiu;
      // se se manteve, o que mudou nele vem em `details`.
      const name = change.itemLabel ?? change.afterLabel ?? change.beforeLabel ?? "—";
      before = change.changeType === "added" ? "—" : change.changeType === "removed" ? name : "";
      after = change.changeType === "removed" ? "—" : change.changeType === "added" ? name : "";
    } else {
      before = formatAuditValue(change.before, change.valueType, translate, locale, change.beforeLabel);
      after = formatAuditValue(change.after, change.valueType, translate, locale, change.afterLabel);
    }

    return {
      field: change.field,
      label: change.label,
      suffix: isItem && change.changeType === "modified" ? change.itemLabel ?? null : null,
      changeType: change.changeType ?? "modified",
      before,
      after,
      details,
      longText: change.valueType === "longtext" || details.some((d: any) => d.after.length > 80),
    } as HistoryChangeVm;
  });
}

export function formatFileSize(size: any, locale: string): string | null {
  const bytes = Number(size);
  if (!Number.isFinite(bytes) || bytes <= 0) return null;
  const units = ["B", "KB", "MB", "GB"];
  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit++;
  }
  return `${new Intl.NumberFormat(locale, { maximumFractionDigits: 1 }).format(value)} ${units[unit]}`;
}

export function initialsOf(name: string | null | undefined): string {
  const parts = `${name ?? ""}`.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "?";
  return ((parts[0][0] ?? "") + (parts.length > 1 ? parts[parts.length - 1][0] : "")).toUpperCase();
}
